import { produce } from 'immer';
import {
  WORKFLOW_EDITOR_INIT,
  WORKFLOW_EDITOR_INIT_CREATE,
  WORKFLOW_EDITOR_INIT_CREATE_WITHWORKFLOW,
  WORKFLOW_GET_REQUEST,
  WORKFLOW_GET_SUCCESS,
  WORKFLOW_UPDATE_REQUEST,
  WORKFLOW_UPDATE_SUCCESS,
  WORKFLOW_GET_FAILURE,
  WORKFLOW_UPDATE_FAILURE,
  WORKFLOW_EDITOR_MAKE_CHANGES,
  WORKFLOW_EDITOR_SELECT_STEP,
  WORKFLOW_EDITOR_SELECT_TRIGGER,
  WORKFLOW_EDITOR_ADD_STEP,
  WORKFLOW_EDITOR_CLONE_STEP,
  WORKFLOW_EDITOR_DELETE_STEP,
  WORKFLOW_EDITOR_STEP_MOVE_UP,
  WORKFLOW_EDITOR_STEP_MOVE_DOWN,
  WORKFLOW_EDITOR_STEP_UPDATE_ID,
  WORKFLOW_EDITOR_STEP_UPDATE_PROPERTY,
  WORKFLOW_EDITOR_STEP_UPDATE_PROPERTY_NAME,
  WORKFLOW_EDITOR_STEP_REMOVE_PROPERTY,
  WORKFLOW_EDITOR_STEP_ADD_PROPERTY,
  WORKFLOW_EDITOR_ADD_TRIGGER,
  WORKFLOW_EDITOR_UPDATE_NAME,
  WORKFLOW_EDITOR_UPDATE_ACTIVE,
  WORKFLOW_TEMPLATES_GET_REQUEST,
  WORKFLOW_TEMPLATES_GET_SUCCESS,
  WORKFLOW_TEMPLATES_GET_FAILURE,
  WORKFLOW_PUBLISHEDAPPS_SUCCESS,
  WORKFLOW_EDITOR_METADATA_UPDATE_INPUTCONFIG,
  WORKFLOW_EDITOR_METADATA_UPDATE_STEP_METADATA,
  WORKFLOW_EDITOR_STEP_MOVE,
  WORKFLOW_TEMPLATE_FOLDERS_GET_REQUEST,
  WORKFLOW_TEMPLATE_FOLDERS_GET_SUCCESS,
  WORKFLOW_TEMPLATE_FOLDERS_GET_FAILURE,
  WORKFLOW_EDITOR_UPDATE_OBJECTLIST,
  WORKFLOW_EDITOR_NEXT_STEP
} from './actions';
import { updatePropertyErrorState } from './validation';
import {
  getNextStepId,
  addStep,
  deleteStep,
  moveStepUp,
  moveStepDown,
  updateStepId,
  updateStep,
  updateStepConfigName,
  removeStepProperty,
  addStepProperty,
  cloneStep,
  metadataUpdateInputConfig,
  metadataUpdateStepMetadata,
  moveStep,
  removeBlankObjectsFromWorkflowStepMetadata
} from './helpers';
import { WorkflowEditorState } from './workflow-types';
import { mergeDeep } from '../helpers/utility';

const initialState: WorkflowEditorState = {
  isFetching: false,
  readOnly: true,
  wizardMode: false,
  wizardComplete: false,
  workflow: null,
  templates: {
    initialized: false
  },
  isDirty: false,
  selectedTrigger: false,
  errorState: {}
};

const initialStateCreate: WorkflowEditorState = {
  isFetching: false,
  readOnly: true,
  wizardMode: false,
  wizardComplete: false,
  workflow: {
    id: '',
    name: 'New Workflow',
    clientId: '',
    active: false,
    trigger: null,
    steps: null,
    lastUpdatedDate: null,
    folderId: null
  },
  templates: {
    initialized: false
  },
  isDirty: false,
  selectedTrigger: false,
  errorState: {}
};

const workfloweditor = (existingState = {}, action): WorkflowEditorState => {
  if (!action) {
    return existingState;
  }
  return produce(existingState, (state: WorkflowEditorState) => {
    switch (action.type) {
      case WORKFLOW_EDITOR_INIT:
        return { ...initialState, ...action.payload };

      case WORKFLOW_EDITOR_INIT_CREATE:
        return { ...initialStateCreate, create: true, readOnly: false };

      case WORKFLOW_EDITOR_INIT_CREATE_WITHWORKFLOW:
        return { ...initialStateCreate, create: true, readOnly: false, wizardMode: true, workflow: action.payload.initialWorkflow };

      case WORKFLOW_GET_SUCCESS:
        return { ...state, ...action.payload };

      case WORKFLOW_GET_REQUEST:
      case WORKFLOW_GET_FAILURE:
      case WORKFLOW_UPDATE_REQUEST:
      case WORKFLOW_UPDATE_SUCCESS:
      case WORKFLOW_UPDATE_FAILURE:
      case WORKFLOW_EDITOR_MAKE_CHANGES:
      case WORKFLOW_PUBLISHEDAPPS_SUCCESS:
        return { ...state, ...action.payload };

      case WORKFLOW_EDITOR_SELECT_STEP: {
        const nextStepId = getNextStepId(state, action.payload.selectedStepId) || null;
        const wizardComplete = state.wizardComplete || nextStepId === null;
        mergeDeep(state, action.payload);
        state.nextStepId = nextStepId;
        state.wizardComplete = wizardComplete;
        break;
      }

      case WORKFLOW_EDITOR_SELECT_TRIGGER: {
        let nextStepId = null;
        if (state.workflow?.steps && state.workflow.steps.length > 0) {
          nextStepId = state.workflow.steps[0].id;
        }
        const wizardComplete = state.wizardComplete || nextStepId === null;
        mergeDeep(state, action.payload);
        state.nextStepId = nextStepId;
        state.wizardComplete = wizardComplete;
        break;
      }

      case WORKFLOW_EDITOR_NEXT_STEP: {
        mergeDeep(state, action.payload);
        break;
      }

      case WORKFLOW_EDITOR_ADD_STEP:
        return produce(state, (state) => {
          addStep(state, action.payload.step, action.payload.stepPosition);
          state.isDirty = true;
        });

      case WORKFLOW_EDITOR_CLONE_STEP:
        cloneStep(state, action.payload.stepId);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_DELETE_STEP:
        deleteStep(state, action.payload.stepId);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_MOVE_UP:
        moveStepUp(state, action.payload.stepId);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_MOVE_DOWN:
        moveStepDown(state, action.payload.stepId);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_MOVE:
        moveStep(state, action.payload.stepId, action.payload.stepPosition);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_UPDATE_ID:
        updateStepId(state, action.payload);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_UPDATE_PROPERTY: {
        updateStep(state, action.payload);
        state.isDirty = true;
        updatePropertyErrorState(state, action.payload);
        break;
      }

      case WORKFLOW_EDITOR_STEP_UPDATE_PROPERTY_NAME:
        updateStepConfigName(state, action.payload);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_REMOVE_PROPERTY:
        removeStepProperty(state, action.payload);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_STEP_ADD_PROPERTY:
        addStepProperty(state, action.payload.propKeyPath, action.payload.value);
        break;

      case WORKFLOW_EDITOR_ADD_TRIGGER:
      case WORKFLOW_EDITOR_UPDATE_NAME:
      case WORKFLOW_EDITOR_UPDATE_ACTIVE:
        mergeDeep(state, { workflow: action.payload });
        state.isDirty = true;
        break;

      case WORKFLOW_TEMPLATES_GET_REQUEST:
      case WORKFLOW_TEMPLATES_GET_SUCCESS:
      case WORKFLOW_TEMPLATES_GET_FAILURE:
        mergeDeep(state, { templates: action.payload });
        break;

      case WORKFLOW_TEMPLATE_FOLDERS_GET_REQUEST:
      case WORKFLOW_TEMPLATE_FOLDERS_GET_SUCCESS:
      case WORKFLOW_TEMPLATE_FOLDERS_GET_FAILURE:
        mergeDeep(state, { templateFolders: action.payload });
        break;

      case WORKFLOW_EDITOR_METADATA_UPDATE_INPUTCONFIG:
        metadataUpdateInputConfig(state, action.payload);
        state.isDirty = true;
        break;

      case WORKFLOW_EDITOR_METADATA_UPDATE_STEP_METADATA:
        metadataUpdateStepMetadata(state, action.payload);
        break;

      case WORKFLOW_EDITOR_UPDATE_OBJECTLIST:
        removeBlankObjectsFromWorkflowStepMetadata(state, action.payload);
        break;
    }
  });
};

export default workfloweditor;
